import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Monitor, Smartphone, Tablet, Tv, X } from 'lucide-react';

/** Petits éléments partagés de la page « Users projets ». */

/**
 * Zones qui défilent en largeur (tableaux, onglets) : un geste surtout vertical (molette, trackpad) fait défiler la page
 * (ou la fiche), un geste horizontal fait défiler la zone en largeur. Sans ça, le trackpad reste « collé » au tableau.
 * → ref à poser sur l'élément.
 */
export function useWheelToPage() {
    const cleanup = useRef(null)
    return useCallback((el) => {
        cleanup.current?.()
        cleanup.current = null
        if (!el) return
        const onWheel = (e) => {
            if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return
            for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
                const oy = getComputedStyle(p).overflowY
                if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
                    p.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY
                    e.preventDefault()
                    return
                }
            }
        }
        el.addEventListener('wheel', onWheel, { passive: false })
        cleanup.current = () => el.removeEventListener('wheel', onWheel)
    }, [])
}

/** Conteneur de tableau qui défile en largeur (petits écrans), sans bloquer le défilement de la page. */
export const HScroll = ({ children }) => {
    const ref = useWheelToPage()
    return <div ref={ref} className='ub-scroll'>{children}</div>
}

export const Badge = ({ tone = 'muted', children, title }) => (
    <span className={`up-badge ${tone}`} title={title}>{children}</span>
)

/** Statut du compte ; suspendedUntil : bloqué temporairement (trop de vidéos coupées sur un autre appareil) jusqu'à cette date. */
export const Status = ({ status, suspendedUntil }) => {
    if (status === 'BLOCKED') return <Badge tone='ko'>Bloqué</Badge>
    if (suspendedUntil && new Date(suspendedUntil) > new Date()) {
        const until = new Date(suspendedUntil).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
        return <Badge tone='warn' title={`Trop de vidéos coupées sur un autre appareil · jusqu'au ${until}`}>Bloqué → {until}</Badge>
    }
    return <Badge tone='ok'>Actif</Badge>
}

const DEVICE_ICONS = { mobile: Smartphone, tablette: Tablet, tv: Tv, ordinateur: Monitor }

/** Appareil lu dans le user-agent : icône + « Mobile · iPhone · Safari » (user-agent complet au survol). */
export const Device = ({ device, ip }) => {
    if (!device && !ip) return <span className='up-muted'>—</span>
    const Icon = DEVICE_ICONS[device?.type] || Monitor
    return (
        <span className='up-device' title={device?.ua || ''}>
            {device && <Icon size={14} />}
            <span>{device?.label || 'Appareil inconnu'}</span>
            {ip && <span className='up-ip'>{ip}</span>}
        </span>
    )
}

/** Pagination : « ‹‹ ‹ page x / y › ›› » + aller à la page. */
export const Pager = ({ page, pages, onPage }) => {
    const [goto, setGoto] = useState('')
    if (pages <= 1) return null
    const go = (p) => onPage(Math.min(pages, Math.max(1, p)))
    return (
        <div className='up-pager'>
            <button disabled={page <= 1} onClick={() => go(1)} title='Première page'><ChevronsLeft size={16} /></button>
            <button disabled={page <= 1} onClick={() => go(page - 1)} title='Précédente'><ChevronLeft size={16} /></button>
            <span>Page <b>{page}</b> / {pages}</span>
            <button disabled={page >= pages} onClick={() => go(page + 1)} title='Suivante'><ChevronRight size={16} /></button>
            <button disabled={page >= pages} onClick={() => go(pages)} title='Dernière page'><ChevronsRight size={16} /></button>
            <form onSubmit={(e) => { e.preventDefault(); if (goto) go(Number(goto)); setGoto('') }}>
                <input type='number' min='1' max={pages} placeholder='Aller à' value={goto} onChange={(e) => setGoto(e.currentTarget.value)} />
            </form>
        </div>
    )
}

/** Fenêtre (fond sombre, Échap ou clic à côté pour fermer). */
export const Modal = ({ title, onClose, children, wide = false }) => {
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose()
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])
    return (
        <div className='modal-overlay up-modal' onClick={onClose}>
            <div className={`modal-box ${wide ? 'lg' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className='up-modal-head'>
                    <p>{title}</p>
                    <button onClick={onClose} className='pay-act-btn' title='Fermer'><X size={18} color='#fff' /></button>
                </div>
                {children}
            </div>
        </div>
    )
}

/** Confirmation : texte + bouton (danger = rouge). confirmText : il faut recopier ce texte pour valider. */
export const Confirm = ({ title, text, action = 'Confirmer', danger = false, confirmText = null, onConfirm, onClose }) => {
    const [typed, setTyped] = useState('')
    const [busy, setBusy] = useState(false)
    const ok = !confirmText || typed.trim().toLowerCase() === confirmText.toLowerCase()
    const run = async () => {
        setBusy(true)
        try { await onConfirm() } finally { setBusy(false) }
        onClose()
    }
    return (
        <Modal title={title} onClose={onClose}>
            <p className='up-text'>{text}</p>
            {confirmText && (
                <>
                    <label className='up-label'>Recopie <b>{confirmText}</b> pour confirmer</label>
                    <input className='up-input' value={typed} onChange={(e) => setTyped(e.currentTarget.value)} autoFocus />
                </>
            )}
            <div className='up-modal-actions'>
                <button className='btn cw' onClick={onClose}>Annuler</button>
                <button className={`btn ${danger ? 'up-danger' : 'cs'}`} disabled={!ok || busy} onClick={run}>{busy ? <span className='loader sm'></span> : action}</button>
            </div>
        </Modal>
    )
}

/** Message en bas de l'écran (succès / erreur), disparaît tout seul. */
export const Toast = ({ toast, onDone }) => {
    useEffect(() => {
        if (!toast) return
        const t = setTimeout(onDone, toast.error ? 6000 : 3500)
        return () => clearTimeout(t)
    }, [toast, onDone])
    if (!toast) return null
    return <div className={`up-toast ${toast.error ? 'ko' : 'ok'}`} onClick={onDone}>{toast.text}</div>
}

export const Empty = ({ children }) => <p className='up-empty'>{children}</p>
